/**
 * LineGraph — DGUI component for line charts (trends over ordered axis).
 */

import {
  Area,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, Typography } from '@mui/material';
import type { LineGraphDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  mt: 1,
  mb: 1,
  overflow: 'hidden',
};

const DEFAULT_COLORS = ['#04377b', '#5c8fd4', '#8fb8e8', '#7b9acc', '#4a7ab8'];

function formatValue(
  value: number,
  format?: 'number' | 'percent' | 'currency',
  currencyCode?: string
): string {
  if (format === 'percent') return `${value}%`;
  if (format === 'currency' && currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${currencyCode} ${value}`;
    }
  }
  return String(value);
}

function normalizeLineProps(props: LineGraphDGUIProps & Record<string, unknown>): LineGraphDGUIProps {
  return {
    ...props,
    valueFormat: (props.valueFormat ?? props.value_format) as LineGraphDGUIProps['valueFormat'],
    currencyCode: props.currencyCode ?? (props.currency_code as string),
    showArea: props.showArea ?? (props.show_area as boolean),
  };
}

export function LineGraph(initialProps: LineGraphDGUIProps & Record<string, unknown>) {
  const {
    title,
    subtitle,
    labels = [],
    series = [],
    valueFormat = 'number',
    currencyCode,
    colors = DEFAULT_COLORS,
    showArea = false,
  } = normalizeLineProps(initialProps);
  const safeLabels = Array.isArray(labels) ? labels : [];
  const safeSeries = Array.isArray(series) ? series : [];
  const isEmpty =
    safeLabels.length === 0 ||
    safeSeries.length === 0 ||
    (safeSeries[0] && (safeSeries[0].data?.length ?? 0) === 0);

  const chartData = safeLabels.map((label, i) => {
    const point: Record<string, string | number> = { label };
    safeSeries.forEach((s) => {
      point[s.name] = Array.isArray(s.data) && s.data[i] != null ? s.data[i] : 0;
    });
    return point;
  });

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600} color="#04377b" gutterBottom>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatValue(v, valueFormat, currencyCode)}
              />
              <Tooltip
                formatter={(value: number) => formatValue(value, valueFormat, currencyCode)}
                labelFormatter={(label) => String(label)}
              />
              {safeSeries.length > 1 && <Legend />}
              {safeSeries.map((s, idx) =>
                showArea && (safeSeries.length === 1 || idx === 0) ? (
                  <Area
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={colors[idx % colors.length]}
                    fill={colors[idx % colors.length]}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                ) : (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={colors[idx % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                )
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
